import type { Company } from "../../../models";
import { ImportTableColumn } from "../../../utilities/types";

/**
 * Represents the visibility and required status of a single column
 */
export interface ColumnSetting {
	visible: boolean;
	required: boolean;
}

/**
 * Configuration for columns that have hardcoded visibility/required rules
 */
export interface HardcodedColumnSetting {
	alwaysVisible?: boolean;
	alwaysRequired?: boolean;
}

// ==========================================================================================
// IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
// Maintain the same order in all places for consistency.
// ==========================================================================================
export const columnDisplayNames: Record<ImportTableColumn, string> = {
	// Basic Information
	grower: "Grower Name",
	farm: "Farm Name",
	field: "Field Name",

	// Attachments
	boundaryGeoJSON: "Boundary File",
	pointsGeoJSON: "Points File",
	zonesGeoJSON: "Zone File",
	fieldNotes: "Field Notes",

	// Order
	isReadyToSample: "Ready to Sample",
	dateFieldReadyEstimated: "Est Field Ready Date",
	fieldPriority: "Field Priority",
	collectBoundary: "Record HD Boundary",
	submissionNotes: "Submission Notes",

	// Deal
	density: "Density",
	depth: "Depth",
	labName: "Lab Name",
	testPackage: "Test Package",
	frequencyOfMicros: "Frequency of Micros",
	deal: "Deal Configuration",

	// Lab Info
	labInstructions: "Instructions for Lab (not for Rogo)",
	microsList: "Samples with Addon Tests",
	lastCrop: "Last Crop",
	nextCrop: "Next Crop",
	yieldGoal: "Yield Goal",

	// Contacts
	branch: "Branch",
	salesperson: "Salesperson",
	salespersonMobilePhone: "Salesperson Mobile Phone",
	growerPhoneNumber: "Grower Phone Number",
	submitterName: "Submitter Name",
	submitterEmail: "Submitter Email",

	// Sync Codes
	eventId: "Event/Sync Code",
	labSubmissionCode: "Lab Submission Code",
	fieldId: "Field ID",
	externalId: "External ID",
	externalFarmId: "External Farm ID",
	externalLabId: "External Lab ID",

	// Misc
	isTestJob: "Test Job",
};

/**
 * Columns that have hardcoded visibility/required rules that cannot be changed
 */
export const hardcodedColumns: Partial<Record<ImportTableColumn, HardcodedColumnSetting>> = {
	grower: { alwaysVisible: true, alwaysRequired: true },
	farm: { alwaysVisible: true },
	field: { alwaysVisible: true, alwaysRequired: true },
	boundaryGeoJSON: { alwaysVisible: true },
	pointsGeoJSON: { alwaysVisible: true },
	collectBoundary: { alwaysVisible: true },
	deal: { alwaysVisible: true, alwaysRequired: true },
	depth: { alwaysVisible: true, alwaysRequired: true },
	density: { alwaysVisible: true, alwaysRequired: true },
};

/**
 * Import flow options that can be toggled per company (maps to portalConfigurationOptions)
 */
export const importFlowOptions: Record<string, string> = {
	"Import: Manual Entry": "Single Upload",
	"Import: SHP": "Bulk Upload",
	"Import: CSV + SHP": "CSV Upload",
	"Import: Proagrica": "Proagrica",
	"Import: Agrian (Echelon)": "Agrian (Echelon)",
	"Import: Agrian (Agellum)": "Agrian (Agellum)",
	// "Import: Agvance": "Agvance",
};

/**
 * ViewModel interface for ImportSettings component
 */
export interface ImportSettingsViewModel {
	// State
	companies: Company[];
	selectedCompany: Company | null;
	sourceCompany: Company | null;
	columnSettings: Record<string, ColumnSetting>;
	loading: boolean;
	saving: boolean;
	copying: boolean;

	// Event handlers
	onSelectCompany: (company: Company | null) => void;
	onSelectSourceCompany: (company: Company | null) => void;
	onColumnChange: (column: ImportTableColumn, field: "visible" | "required", value: boolean) => void;
	onSave: () => Promise<void>;
	onCopySettings: () => void;
	onResetToDefaults: () => void;
	onSetAllVisible: (visible: boolean) => void;
	onSetAllRequired: (required: boolean) => void;
	onCancel: () => void;

	// Computed values
	allNonHardcodedVisible: boolean;
	allVisibleNonHardcodedRequired: boolean;
	hasChanges: boolean;

	// Helper functions for view
	isVisibleDisabled: (column: ImportTableColumn) => boolean;
	isRequiredDisabled: (column: ImportTableColumn) => boolean;
	getHardcodedLabel: (column: ImportTableColumn) => string | null;

	// Import flow settings
	importFlowSettings: Record<string, boolean>;
	onImportFlowChange: (option: string, checked: boolean) => void;

	// Permission
	isSuperUser: boolean;
}
